import { Sort } from '@angular/material/sort';

export type TableSortValue = string | number | boolean | Date | null | undefined;
export type TableSortAccessors<T> = Record<string, (row: T) => TableSortValue>;

const collator = new Intl.Collator(undefined, {
  numeric: true,
  sensitivity: 'base',
});

/**
 * Sorts a copy of a table's rows while keeping blank values at the end.
 * Column-specific accessors let the displayed value (for example, Active)
 * determine the order instead of an internal code.
 */
export function sortTableRows<T extends Record<string, any>>(
  rows: readonly T[],
  sort: Sort,
  accessors: TableSortAccessors<T> = {}
): T[] {
  if (!sort.active || !sort.direction) return [...rows];

  const accessor = accessors[sort.active] ?? ((row: T) => row[sort.active]);
  const direction = sort.direction === 'asc' ? 1 : -1;

  return rows
    .map((row, index) => ({ row, index }))
    .sort((left, right) => {
      const leftValue = accessor(left.row);
      const rightValue = accessor(right.row);
      const leftBlank = leftValue === null || leftValue === undefined || leftValue === '';
      const rightBlank = rightValue === null || rightValue === undefined || rightValue === '';

      if (leftBlank && rightBlank) return left.index - right.index;
      if (leftBlank) return 1;
      if (rightBlank) return -1;

      const comparison = compareValues(leftValue, rightValue);
      return comparison === 0 ? left.index - right.index : comparison * direction;
    })
    .map(({ row }) => row);
}

function compareValues(left: Exclude<TableSortValue, null | undefined>, right: Exclude<TableSortValue, null | undefined>): number {
  if (left instanceof Date || right instanceof Date) {
    return toTimestamp(left) - toTimestamp(right);
  }

  if (typeof left === 'number' && typeof right === 'number') {
    return left - right;
  }

  if (typeof left === 'boolean' && typeof right === 'boolean') {
    return Number(left) - Number(right);
  }

  return collator.compare(String(left), String(right));
}

function toTimestamp(value: Exclude<TableSortValue, null | undefined>): number {
  if (value instanceof Date) return value.getTime();
  const timestamp = new Date(String(value)).getTime();
  return Number.isNaN(timestamp) ? 0 : timestamp;
}
